/*--------------------------------------------------------------
>>> TABLE OF CONTENTS:
----------------------------------------------------------------
* Dynamic Library Import (dynamically import google maps library)
* Fetch site's location data
* Load Map data (initMap function)
	* MapdId
	* Generate user controls (legend or filter)
	* Filter locations by multiple criteria (doctor and/or service)
	* Creates and shows a dialog when no results are found
	* Generate Pin/Marker Styling
	* Generate info panel content when marker or nav buttons are click
	* Show Distance From Me function
	* Add markers to the map
	* Add click event to info panel's back/close button
	* Mobile view toggle function - toggle between map and list
	* Toggle panels open/closed
	* Add DNI to generated phone numbers
* Replace {placeholders}
* Sort locations based on sort fields
* Generate navigation panel content
--------------------------------------------------------------*/


/**
 * Dynamic Library Import (dynamically import google maps library)
 * @see https://developers.google.com/maps/documentation/javascript/load-maps-js-api#dynamic-library-import
 */
(g => {var h, a, k, p = "The Google Maps JavaScript API", c = "google", l = "importLibrary", q = "__ib__", m = document, b = window; b = b[c] || (b[c] = {}); var d = b.maps || (b.maps = {}), r = new Set, e = new URLSearchParams, u = () => h || (h = new Promise(async (f, n) => {await(a = m.createElement("script"));e.set("libraries", [...r] + "");for (k in g) e.set(k.replace(/[A-Z]/g, t => "_" + t[0].toLowerCase()), g[k]);e.set("callback", c + ".maps." + q);a.src = `https://maps.${c}apis.com/maps/api/js?` + e;d[q] = f;a.onerror = () => h = n(Error(p + " could not load."));a.nonce = m.querySelector("script[nonce]")?.nonce || "";m.head.append(a)}));d[l] ? console.warn(p + " only loads once. Ignoring:", g) : d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n))})({
    key: mapData.maps_key,
    v: "weekly", // Version to use (weekly, quarterly, beta, alpha, etc.).
});


/**
 * Fetch site's location data
 * @see https://github.com/Fuel-Digital/fuelLOCATIONS/blob/c7b31e54467c5e3d0837ab91c8c93e34bf03ce44/js/map.js#L59
 */
fetch(window.location.origin+'/wp-json/fm/v1/map').then((data) => data.text()).then((data) => {
	const result = JSON.parse(data);
    locations = result.locations; // Get location data
	legend = Array.isArray(result.legend) ? result.legend : Object.values(result.legend || {}); // Get legend data
    docs = result.doc_filter; // Get docs data
	services = result.service_tags; // Get services data
	const fm_mapContainers = document.querySelectorAll('.google-map-container'); // Get all map wrappers

    // After location data is fetched, get all map wrappers and add intersection observer
	fm_mapContainers.forEach(container => observer.observe(container));
}).catch((error) => { console.log(error); });


// Use IntersectionObserver to lazy load the map when it is 100px below the viewport
const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            initMap(entry.target);
            observer.unobserve(entry.target);
        }
    }); 
}, { threshold: [0], rootMargin: '100px' });


/**
 * Load Map data (initMap function)
 */
async function initMap(element) {
	const dataset = element.parentElement.dataset;
	const options = JSON.parse(dataset.options); // Get map options from the element
	const pin = JSON.parse(dataset.marker); // Get map marker options from the element
	const infoPanelHeader = dataset.infopanel_header; // Get info panel header content from the element
	const infoPanelCTA = dataset.infopanel_cta; // Get info panel call to action content from the element
	const infoPanelCol1 = dataset.infopanel_col1; // Get info panel column 1 content from the element
	const infoPanelCol2 = dataset.infopanel_col2; // Get info panel column 2 content from the element
	const infoPanelFooter = dataset.infopanel_footer; // Get info panel footer content from the element
	const filterControls = element.parentElement.querySelector('.map-filter-panel');
	const filterContent = element.parentElement.querySelector('.filter-contents');
	const navPanel = element.parentElement.querySelector('.map-nav-panel');
	const infoPanel = element.parentElement.querySelector('.map-info-panel');
	const locContent = element.parentElement.querySelector('.map-info');
	const mapContainer = element.parentElement.querySelector('.google-map-container');
	const filterState = { doctor: '', service: '' }; // Store the current filter state
	const blockID = Math.random().toString(36).substring(2, 4);
	const mediaQuery = window.matchMedia('(min-width: 50em)'); // 50em = 800px assuming 1em = 16px

    // Request libraries when needed, not in the script tag.
    const { Map } = await google.maps.importLibrary("maps");
    const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker");
	const { Spherical } = await google.maps.importLibrary("geometry"); // Import the geometry library

    const infoWindow = new google.maps.InfoWindow();
    let bounds = new google.maps.LatLngBounds(); // Create bounds object
	let toggleView = true; // Toggle view between map and list
	const markers = []; // markers array holds the marker data for navigation panel button action

	// Generate navigation panel
	let listSort = JSON.parse(dataset.list_options); // Get nav list layout from the element
	let navLayout = dataset.list_layout; // Get nav list layout from the element
	let cat = listSort.category; // get category from the element

	// If category is set, filter locations to only show that category else show all locations
	catLocations = cat ? locations.filter(location => location.location_categories.some(category => category.slug === cat)) : locations;

	// Sort locations before generating navigation list
	const sortedLocations = locationSort(catLocations, [
		{ orderby: listSort.list_orderby1, order: listSort.list_order1 },
		{ orderby: listSort.list_orderby2, order: listSort.list_order2 }
	]);
	
	// Generate the nav panel content and update the DOM
	navPanel.innerHTML = generateNavPanel(sortedLocations, navLayout, listSort);


	/**
	 * MapdId
	 * mapId can be any random string, it is required for the updated Google maps API, see link below for Fuel map IDs (Alt test ID: 11e29a4654432462 ID: f80de3e16757d3b7)
	 * @see https://console.cloud.google.com/google/maps-apis/studio/maps?inv=1&invt=AbjO4Q&project=fuel-theme 
	 */
    const mapOptions = {
        mapId: dataset.mapid || blockID,
		...options, // Spread Operator itterates through options array (from data-options attribute) to add each key/value to mapOptions object
    };

    const map = new Map(element, mapOptions);


	/**
	 * Generate user controls (legend or filter)
	 * @param {Object} options - Configuration options for the control.
	 * @param {string} options.type - The type of control ("legend", "filter").
	 * @param {string} options.position - The Google Maps ControlPosition for the control.
	 * @param {string} options.heading - The heading text for the control.
	 * @param {Array} options.items - The items to populate the control (legend or filter options).
	 * @param {Function} [options.onChange] - Optional callback for the "change" event on select boxes.
	 */
	function generateUserControl({ type, position, heading, items, onChange }) {
		// Create the control div element
		const controlDiv = document.createElement('div');
		controlDiv.className = `${type === 'legend' ? 'map-controls' : 'filter-controls'} ${type} ${heading?.split(' ')[2]}`;

		// Create and append the heading
		if (heading) {
			const headingEl = (type === 'legend') ? document.createElement('h3') : document.createElement('label');
			headingEl.htmlFor = `${blockID}_${heading.split(' ')[2]}`;
			headingEl.textContent = heading;
			controlDiv.appendChild(headingEl);
		}
		
		// Create content based on the control type
		if (type === 'legend') {
			// Populate the legend with icons and names
			controlDiv.innerHTML += items
				.filter(item => item.icon)
				.map(item => `<div><img src="${item.icon}" alt="${item.name}"><span>${item.name}</span></div>`)
				.join('');
		} else if (type === 'filter') {
			// Create a select element for filtering
			const select = document.createElement('select');
			select.id = `${blockID}_${heading.split(' ')[2]}`;
			
			// Add a default option
			const defaultOption = document.createElement('option');
			defaultOption.value = '';
			defaultOption.textContent = `All ${heading.split(' ')[2]}`; // e.g., "All Providers" or "All Services"
			select.appendChild(defaultOption);

			// Add options to the select
			items.forEach(item => {
				if(item.slug || item.value) {
					const option = document.createElement('option');
					option.value = item.slug || item.value; // Use `slug` or `value` depending on the item structure
					option.textContent = item.name || item.label; // Use `name` or `label`
					select.appendChild(option);
				}
			});

			// Append select to the control div
			controlDiv.appendChild(select);

			// Add event listener if a callback is provided
			if (onChange) {
				select.addEventListener('change', event => onChange(event.target.value));
			}
		} else if (type === 'distance') {
			const distance = document.createElement('button');
			distance.className = 'get-my-location button';
			distance.textContent = 'Show Distance From Me';
			controlDiv.appendChild(distance);
		}

		// Add the control to the map
		if (type === 'legend') {
			map.controls[google.maps.ControlPosition[position]].push(controlDiv);
		} else {
			filterContent.appendChild(controlDiv);
		}
	}

	// Generate legend
	if (options.legend) {
		generateUserControl({
			type: 'legend',
			position: 'RIGHT_TOP',
			heading: 'Legend',
			items: legend
		});
	}

	// Generate distance filter
	if (listSort.show_my_location) {
		generateUserControl({
			type: 'distance',
		});
	}

	// Generate filter by providers
	if (listSort.filter_by_provider) {
		generateUserControl({
			type: 'filter',
			heading: 'Browse by Providers',
			items: docs,
			onChange: selectedDoctor => {
				filterState.doctor = selectedDoctor;
				filterLocations(filterState);
			}
		});
	}

	// Generate filter by services
	if (listSort.filter_by_services) {
		generateUserControl({
			type: 'filter',
			heading: 'Browse by Services',
			items: services,
			onChange: selectedService => {
				filterState.service = selectedService;
				filterLocations(filterState);
			}
		});
	}


	/**
	 * Filter locations by multiple criteria (doctor and/or service) and update the UI
	 * @param {Object} filters - The filters to apply.
	 * @param {string} [filters.doctor] - The selected doctor IDs (comma-separated).
	 * @param {string} [filters.service] - The selected service slug.
	 */
	function filterLocations(filters = {}) {
		const { doctor, service } = filters;

		// If no filters are provided, show all locations
		if (!doctor && !service) {
			navPanel.innerHTML = generateNavPanel(sortedLocations, navLayout, listSort); // Generate the nav panel content and update the DOM
			addMarkers(sortedLocations); // Add markers to the map
			return;
		}

		// Filter locations based on the criteria
		let filteredLocations = sortedLocations;

		// Filter by doctor if provided
		if (doctor) {
			const selectedIds = doctor.split(",").map(id => parseInt(id.trim(), 10));
			filteredLocations = filteredLocations.filter(location =>
				selectedIds.includes(location.id)
			);
		}

		// Filter by service if provided
		if (service) {
			filteredLocations = filteredLocations.filter(location =>
				location.services.some(serviceObj => serviceObj.slug === service)
			);
		}

		 // If no locations match the filters, show an alert
		 if (filteredLocations.length === 0) {
			showNoResultsDialog();
			return;
		}

		// Update navigation panel
		navPanel.innerHTML = generateNavPanel(filteredLocations, navLayout, listSort);

		// Update map markers
		addMarkers(filteredLocations);
	}


	/**
	 * Creates and shows a dialog when no results are found
	 */
	function showNoResultsDialog() {
		// Create the dialog element
		const dialog = document.createElement("dialog");
		dialog.id = "noResultsDialog";

		// Create the dialog content
		const dialogContent = document.createElement("p");
		dialogContent.textContent = "No locations match the selected filters.";

		// Create the reset filters button
		const resetButton = document.createElement("button");
		resetButton.textContent = "Reset Filters";

		// Reset the filters and close the dialog when the reset button is clicked
		resetButton.addEventListener("click", () => {
			// Reset the filter selections to "All Providers" and "All Services"
			element.parentElement.querySelector('.Providers select').value = ''; // Reset doctor filter
			element.parentElement.querySelector('.Services select').value = ''; // Reset service filter

			// Clear the filter state
			filterState.doctor = '';  // Clear the doctor filter state
			filterState.service = ''; // Clear the service filter state
			
			// Call filterLocations to apply the reset (show all locations)
			filterLocations({ doctor: '', service: '' });

			// Close the dialog and remove it from the DOM
			dialog.close();
			dialog.remove();
		});

		// Append the content and reset button to the dialog
		dialog.appendChild(dialogContent);
		dialog.appendChild(resetButton);

		// Append the dialog to the body
		document.body.appendChild(dialog);

		// Show the dialog
		dialog.showModal();
	}


	/**
	 * Generate Pin/Marker Styling
	 * Function to generate PinElement marker styling based on style type (active or default)
	 * @see https://developers.google.com/maps/documentation/javascript/examples/advanced-markers-basic-style
	 * @see https://developers.google.com/maps/documentation/javascript/examples/advanced-markers-graphics
	 */
	function generatePinStyle(loc, isActive = false) {
		// Case 1: If marker_style is 'styled' and category_markers is false, return the styled marker
		if (pin.marker_style === 'styled' && !pin.category_markers) {
			return new PinElement({
				scale: pin.marker_scale,
				background: isActive ? pin.active_marker_background_color : pin.marker_background_color,
				borderColor: isActive ? pin.active_marker_border_color : pin.marker_border_color,
				glyphColor: isActive ? pin.active_marker_glyph_color : pin.marker_glyph_color,
			}).element;
		}
	
		// Case 2: If category_markers are enabled, attempt to use SVG category icons
		if (pin.category_markers) {
			const category = loc.location_categories.find(cat => cat.icon);
			const legendItem = legend.find(item => item.name === category?.name);
			const iconState = legendItem ? (isActive ? legendItem.svg2 : legendItem.svg1) : null;
	
			if (iconState) {
				const svgElement = new DOMParser().parseFromString(iconState, "image/svg+xml").documentElement;
				const [originalWidth, originalHeight] = [parseFloat(svgElement.getAttribute("width") || 0), parseFloat(svgElement.getAttribute("height") || 0)];
				if (originalWidth && originalHeight) {
					const scale = pin.marker_scale;
					svgElement.setAttribute("width", `${originalWidth * scale}`);
					svgElement.setAttribute("height", `${originalHeight * scale}`);
					if (!svgElement.getAttribute("viewBox")) svgElement.setAttribute("viewBox", `0 0 ${originalWidth} ${originalHeight}`);
				}
				return svgElement;
			} else if (pin.marker_style === 'styled') {
				// Fallback to styled marker if no category icon exists
				return new PinElement({
					scale: pin.marker_scale,
					background: isActive ? pin.active_marker_background_color : pin.marker_background_color,
					borderColor: isActive ? pin.active_marker_border_color : pin.marker_border_color,
					glyphColor: isActive ? pin.active_marker_glyph_color : pin.marker_glyph_color,
				}).element;
			}
		}
	
		// Case 3: Fallback to the default Google marker
		return null;
	}
	

	/**
	 * Generate info panel content when marker or nav buttons are click
	 */
	function generateInfoPanel(index) {
		// Check if infoPanelCol2 is empty
		const col2 = replacePlaceholders(infoPanelCol2, sortedLocations[index]) !== '<div class="infoPanel-padding"></div>';

		let output = 
			'<div class="map-loc-header">' + replacePlaceholders(infoPanelHeader, sortedLocations[index]) + '</div>'+
			replacePlaceholders(infoPanelCTA, sortedLocations[index])+
			'<div class="map-loc-info">'+
				(col2 ? '<div class="col1">' : '') + 
				replacePlaceholders(infoPanelCol1, sortedLocations[index])+
				(col2 ? '</div><div class="col2">' : '')+
				replacePlaceholders(infoPanelCol2, sortedLocations[index])+
				(col2 ? '</div>' : '')+
			'</div>'+
			'<div class="map-loc-footer">' + replacePlaceholders(infoPanelFooter, sortedLocations[index]) + '</div>';
		return output;
	}

	// Add markers
	addMarkers(sortedLocations);


	/**
	 * Show Distance From Me function
	 * Filter locations by distance
	 * @see https://developers.google.com/maps/documentation/javascript/geolocation
	 * @see https://developers.google.com/maps/documentation/javascript/examples/circle-simple
	 */
	let currentCircle = null;  // Declare a variable to hold the circle reference
	if (listSort.show_my_location) {
		element.parentElement.querySelector('.get-my-location')?.addEventListener('click', (e) => {
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition(
					(position) => {
						const userPos = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
		
						infoWindow.setPosition(userPos);
						infoWindow.setContent("Your Location.");
						infoWindow.open(map);

						bounds.extend(userPos);
						map.fitBounds(bounds);

						// Clear the previous circle if it exists
						if (currentCircle) {
							currentCircle.setMap(null);
						}

						// Create a new circle and store the reference
						currentCircle = new google.maps.Circle({
							strokeColor: "#FF0000",
							strokeOpacity: 0.8,
							strokeWeight: 2,
							fillColor: "#FF0000",
							fillOpacity: 0.35,
							map,
							center: userPos,
							radius: 200,
						});

						/* new google.maps.marker.AdvancedMarkerElement({
							position: userPos,
							title: "Your Location",
							map: map,
						}); */
 
						// Get only the map-nav-item list items
						const mapNavItems = [...navPanel.querySelectorAll('li.map-nav-item')];

						// Calculate distances and store them in an array
						const distances = mapNavItems.map((li) => {
							const button = li.querySelector('button[data-index]');
							const index = parseInt(button.dataset.index, 10);
							const markerPos = markers[index].position;
							const distanceInMeters = google.maps.geometry.spherical.computeDistanceBetween(userPos, markerPos);
							const distanceInMiles = distanceInMeters / 1609.344; // Convert meters to miles

							// Add distance as a data attribute for easier sorting
							li.dataset.distance = distanceInMiles;

							// Append distance to the button if not already present
							if (!button.querySelector('.marker-distance')) {
								const distanceNode = document.createElement('small');
								distanceNode.classList.add('marker-distance');
								distanceNode.textContent = `${distanceInMiles.toFixed(2)} miles`;
								button.appendChild(distanceNode);
							}

							return { li, distanceInMiles };
						});

						// Sort items by distance if more then 1 location is found
						if (mapNavItems.length > 1) {
							distances.sort((a, b) => a.distanceInMiles - b.distanceInMiles);

							// Reorder the list in the DOM
							const fragment = document.createDocumentFragment();
							distances.forEach(({ li }) => fragment.appendChild(li)); // Append sorted items
							navPanel.innerHTML = ''; // Clear the list
							navPanel.appendChild(fragment); // Add sorted items back
						}
					}, (error) => {
						// Handle errors
						switch (error.code) {
						  case error.PERMISSION_DENIED:
							alert("Please enable location services.");
							break;
						  case error.POSITION_UNAVAILABLE:
							alert("Location information is unavailable.");
							break;
						  case error.TIMEOUT:
							alert("The request to get your location timed out.");
							break;
						  default:
							alert("An unknown error occurred.");
						}
					  }, {
						enableHighAccuracy: false, // Set to false for faster retrieval
						timeout: 5000,            // Maximum wait time
						maximumAge: 30000         // Use cached data if available within 30 seconds
					}
				);
			} else { console.warn('Please allow location access') }
		}); // End of Show Distance From Me function
	}


	/**
	 * Add markers to the map
     * As of February 21st, 2024 (v3.56), google.maps.Marker is deprecated, using AdvancedMarkerElement instead.
	 * @param {*} locations
     * @see https://developers.google.com/maps/documentation/javascript/advanced-markers/migration
	 * @see https://developers.google.com/maps/documentation/javascript/advanced-markers/basic-customization
	 * @see https://developers.google.com/maps/documentation/javascript/examples/advanced-markers-simple
	 * @see https://stackoverflow.com/questions/2989858/google-maps-v3-enforcing-min-zoom-level-when-using-fitbounds
     */
	function addMarkers(locations) {
		// Clear existing markers from the map
		markers.forEach(marker => marker.setMap(null));
		markers.length = 0; // Reset the markers array

		// Reset bounds only if desktop or map is in view on mobile
		if (mediaQuery.matches || !toggleView) { 
			bounds = new google.maps.LatLngBounds(); // Clear the bounds completely
		}

		// Prevent the map from zooming in too close when filters only show one location (see accepted answer on stackoverflow link above)
		map.maxDefaultZoom = 18;
		google.maps.event.addListenerOnce(map, "bounds_changed", function () {
			this.setZoom(Math.min(this.getZoom(), this.maxDefaultZoom));
		});
	
		// Loop through the provided locations and add markers
		locations.forEach((location, index) => {
			const position = new google.maps.LatLng(location.lat, location.lng); // Create position object
	
			// Create and place the marker
			const marker = new AdvancedMarkerElement({
				map: map,
				position: position,
				title: location.name,
				content: generatePinStyle(location),
			});
	
			// Extend the bounds to include the current marker's position
			bounds.extend(position);
	
			// Add pin placement animation
			const delayTime = 0.2 + Math.random(); // Random delay for animation
			marker.content.style.setProperty("--delay-time", `${delayTime}s`);
			marker.content.classList.add("drop");
	
			// Add a click event listener for the marker
			marker.addListener("click", () => {
				// Get the index of the clicked marker
				const clickedMarkerIndex = markers.indexOf(marker);
				map.setZoom(18);
				map.setCenter(marker.position);
				togglePanels();
				locContent.innerHTML = generateInfoPanel(clickedMarkerIndex);
			});
	
			// Add the marker to the markers array
			markers.push(marker);
		});
	
		// Fit the map to the bounds of the markers
		map.fitBounds(bounds);

		// Add click event to location navigation panel buttons
		element.parentElement.querySelectorAll('.map-nav-item-btn').forEach(btn => {
			const index = btn.getAttribute('data-index');

			btn.addEventListener('click', (e) => {
				google.maps.event.trigger(markers[index], 'click');
				e.target.ariaExpanded = true;
			});

			// Add hover event to location navigation panel buttons
			btn.addEventListener('mouseover', () => {
				markers[index].content = generatePinStyle(sortedLocations[index], 'active');
				markers[index].content.classList.remove("drop");
				options.bounce && markers[index].content.classList.add("bounce");
			});

			// Revert to original pin style
			btn.addEventListener('mouseout', () => {
				markers[index].content = generatePinStyle(sortedLocations[index]);
				markers[index].content.classList.remove("bounce");				
			});
			
		});
	} // End of addMarkers function
	

	/**
	 * Add click event to info panel's back/close button
	 * */ 
	element.parentElement.querySelectorAll('.map-back').forEach(backButton => {
		backButton.addEventListener('click', () => {
			infoWindow.close();
			navPanel.querySelector('[aria-expanded="true"]')?.setAttribute('aria-expanded', 'false');
			map.fitBounds(bounds);
			map.setTilt(options.tilt);
			togglePanels();
		});
	});


	/**
	 * Mobile view toggle function - toggle between map and list
	 */ 
	element.parentElement.querySelector('.map-mobile-view-bar')?.addEventListener('click', (e) => {
		mapContainer.style.zIndex = toggleView ? "1" : "0";
		e.target.innerHTML = toggleView ? "View List" : "View Map"; // Toggle button text
		toggleView = !toggleView;
		togglePanels(true);
	});


	/**
	 * Toggle panels open/closed
	 * @param {boolean} e - Force the infoPanel to be hidden (used on mobile map/list toggle)
	 */ 
	function togglePanels(e) {
		// Check if the infoPanel is currently visible (not hidden)
		const isInfoPanelVisible = e ? true : !infoPanel.hidden;
	
		// If the infoPanel is visible, hide it and show the filterControls and navPanel, otherwise, show the infoPanel and hide the other panels
		if(filterControls) filterControls.hidden = isInfoPanelVisible ? false : true;
		navPanel.hidden = isInfoPanelVisible ? false : true;
		infoPanel.hidden = isInfoPanelVisible ? true : false;

		// Adjust the zIndex for the infoPanel. Set zIndex to "1" when the infoPanel is shown and "0" when it's hidden
		infoPanel.style.zIndex = isInfoPanelVisible ? "0" : "2";
	}


	/**
	 * Add DNI to generated phone numbers
	 */
	if (typeof dniLoadingTimer === 'function') {
		dniLoadingTimer(); // Call DNI function if it exists

		const handleMapMutations = (mutations) => {
			mutations.forEach(({ type, target }) => {
				if (type === 'childList') {
					// Call DNI function for new phone elements without the 'dni' class
					target.querySelectorAll('a.phone:not(.dni)').forEach(phone => {
						phone.classList.add('dni');
						dniLoadingTimer();
					});
				}
			});
		};

		const dniObserver = new MutationObserver(handleMapMutations);
		dniObserver.observe(infoPanel, { childList: true, subtree: true });
	}
} // End of initMap


/**
 * Replace {placeholders}
 * {key|prefix|suffix}
 * - key: The data key to fetch.
 * - prefix: Text to prepend to the value (optional).
 * - suffix: Text to append to the value (optional).
 **/ 
function replacePlaceholders(template, data) {
	return template.replace(/{(.*?)}/g, (_, placeholder) => {
		const [key, prefix = '', suffix = ''] = placeholder.split('|');
		const keys = key.split('.');
		let value = data;

		// Traverse the keys to fetch the value
		for (const k of keys) {
			if (value && typeof value === 'object' && k in value) { value = value[k]; } else { return ''; } // If Key does not exist, replace with an empty string
		}

		// Check if the value is empty, null, or false
		if (!value && value !== 0) { return '';} // Treat as empty and return nothing

		// Convert objects/arrays to strings if necessary
		value = Array.isArray(value) || typeof value === 'object' ? JSON.stringify(value) : value;

		// Apply prefix and suffix
		return `${prefix}${value}${suffix}`;
	}); 
}


/**
 * Sort locations based on sort fields
 **/
function locationSort(locations, sortFields) {
    return locations.sort((a, b) => {
        for (let { orderby, order } of sortFields) {
            const aValue = a[orderby]?.toString().toLowerCase();
            const bValue = b[orderby]?.toString().toLowerCase();

            if (aValue < bValue) return order === 'ASC' ? -1 : 1;
            if (aValue > bValue) return order === 'ASC' ? 1 : -1;
        }
        return 0; // If all sort criteria are equal
    });
}


/**
 * Generate navigation panel content
 **/
function generateNavPanel(locations, navLayout, listSort) {
    // Generate the rest of the navigation panel items
    let innerNavPanel = locations.map((location, index) => `
        <li class="map-nav-item">
            <button aria-expanded="false" aria-haspopup="true" 
                    class="map-nav-item-btn marker-trigger" 
                    data-index="${index}" 
                    data-id="${location.id}">
                ${replacePlaceholders(navLayout, location)}
            </button>
        </li>
    `).join('');

    // Return the complete HTML string
    return innerNavPanel;
}